import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppModule } from '../../app.module';
import { IncidentService } from '../../shared/incident.service';
import { EtaConfirm, Incident, Document, IncidentOperator } from '../../models/incident';
import { TyreLocation, UserType } from '../../shared/enums';
import { DocumentCategory } from '../../models/user';
import { ImageQueueService, QueuedImage } from '../../shared/image-queue.service';
import { DriverService } from '../../shared/driver.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ReportService } from '../../shared/report.service';
import { Base64Report } from '../../models/report';
import { environment } from '../../../environments/environment';
import { IncidentChat } from '../../shared/incident-chat/incident-chat';
import { ChatSenderType } from '../../models/chat';
import { WebsocketsService } from '../../shared/websockets.service';
import { ChatAlertsService } from '../../shared/chat-alerts.service';

@Component({
  selector: 'app-bdtech-unauthed-dash',
  imports: [AppModule, IncidentChat],
  providers: [ChatAlertsService],
  templateUrl: './bdtech-unauthed-dash.html',
  styleUrl: './bdtech-unauthed-dash.less'
})
export class BdtechUnauthedDash implements OnInit, OnDestroy {
  readonly chatSender = ChatSenderType.ServiceProvider;

  public readonly DESCRIPTION_MAX_LENGTH = 100;
  private readonly MODAL_STATE_KEY = 'bdtech_incident_photos_open';
  private readonly CAMERA_INTENT_KEY = 'bdtech_camera_launched_timestamp';

  public incident: Incident | null = null;
  public isLoading: boolean = true;
  public mapUrl: SafeResourceUrl | null = null;

  public declined: boolean = false;
  public isAccepting: boolean = false;
  public isCommittingEta: boolean = false;
  public showFullDescription: boolean = true;
  public showDriverDetails: boolean = false;
  public showOperatorDetails: boolean = false;

  // ETA calculator
  public travelTimeMinutes: number | null = null;
  public prepTimeMinutes: number = 0;
  public delayMinutes: number = 0;

  // Arrival
  public isSettingArrival: boolean = false;
  public isUpdateArrivalModalVisible: boolean = false;

  // Delay
  public isAddDelayModalVisible: boolean = false;
  public isAddingDelay: boolean = false;
  public delayModalMinutes: number = 15;
  public delayModalReason: string = 'Bad Road Conditions';
  public readonly delayReasons: string[] = [
    'Bad road conditions',
    'Delayed on another breakdown',
    'Detour for stock',
    'Doing induction',
    'Truck is still inside (mine, factory, offloading)',
    'Mechanical team is still busy',
    'Security check',
    'Bad Weather Conditions',
    'Other'
  ];

  // POW
  public isLoadingProofOfWork: boolean = false;

  // Images
  public isViewImagesModalVisible: boolean = false;
  public removingDocumentId: number | null = null;
  public isCompleteIncidentModalVisible: boolean = false;
  public isCompletingIncident: boolean = false;
  public pendingImages: QueuedImage[] = [];
  public imageCategories: DocumentCategory[] = [];
  private queueInterval: any = null;
  private onlineHandler: (() => void) | null = null;
  private queueSubscriptions: Subscription[] = [];

  private routeSub: Subscription | null = null;
  private websocketSubscription: Subscription | null = null;
  private incidentLoadVersion = 0;
  public currentIncidentId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private incidentService: IncidentService,
    private sanitizer: DomSanitizer,
    public imageQueueService: ImageQueueService,
    private driverService: DriverService,
    private notification: NzNotificationService,
    private reportService: ReportService,
    private websockets: WebsocketsService,
    private chatAlerts: ChatAlertsService
  ) {}

  @HostListener('document:click')
  enableChatAlerts(): void {
    this.chatAlerts.enable();
  }

  ngOnInit(): void {
    this.loadDocumentCategories();
    this.routeSub = this.route.params.subscribe(params => {      
      const incidentId = +params['incidentId'];
      this.websocketSubscription?.unsubscribe();
      this.websocketSubscription = null;
      this.currentIncidentId = incidentId;
      if (Number.isSafeInteger(incidentId) && incidentId > 0) {
        this.websocketSubscription = this.websockets.connectIncident(incidentId, UserType.serviceProviderOperator).subscribe(msg => {
          const incident = this.websockets.getIncidentUpdate(msg, this.currentIncidentId);
          if (incident && this.incident?.incidentId === this.currentIncidentId) {
            this.incidentLoadVersion++;
            this.applyIncident(incident, true);
            this.isLoading = false;
          }
          const chatUpdate = this.websockets.handleChatMessage(msg, this.chatSender, this.currentIncidentId);
          if (chatUpdate) this.chatAlerts.notify(chatUpdate);
        });
      }
      
      // 1. Check if we are recovering from a camera launch
      const isRecovering = this.checkAndRestoreModalState(incidentId);
      
      // 2. Load the incident (pass a flag to force fresh data if recovering)
      this.loadIncident(incidentId, isRecovering);
    });
  }

  private checkAndRestoreModalState(currentIncidentId: number): boolean {
  const savedIncidentId = sessionStorage.getItem(this.MODAL_STATE_KEY);
  const cameraLaunched = sessionStorage.getItem(this.CAMERA_INTENT_KEY);
  
  if (savedIncidentId && parseInt(savedIncidentId, 10) === currentIncidentId) {
    // Re-open the modal
    this.isViewImagesModalVisible = true;
    
    if (cameraLaunched) {
      // Show a notification so the user knows what happened
      this.notification.warning(
        'Session Restored', 
        'Your device temporarily suspended the browser. Please take the photo again.',
        { nzDuration: 5000 }
      );
    }
    
    // Clean up
    sessionStorage.removeItem(this.CAMERA_INTENT_KEY);
    return true; 
  }
  
  return false;
}

  ngOnDestroy(): void {
    this.incidentLoadVersion++;
    this.websocketSubscription?.unsubscribe();
    this.routeSub?.unsubscribe();
    if (this.queueInterval) {
      clearInterval(this.queueInterval);
      this.queueInterval = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    this.queueSubscriptions.forEach(sub => sub.unsubscribe());
    this.queueSubscriptions = [];
  }

  private loadDocumentCategories(): void {
    this.driverService.getDocumentCategories().subscribe({
      next: (data) => { this.imageCategories = data; },
      error: () => {}
    });
  }

  private initImageQueue(incidentId: number): void {
    this.imageQueueService.resetStuckUploads(incidentId)
      .then(() => this.imageQueueService.getPendingImagesForIncident(incidentId))
      .then((pending) => {
        this.pendingImages = pending;
        this.imageQueueService.processQueueNoToken(incidentId);
      });

    this.queueInterval = setInterval(() => {
      this.imageQueueService.processQueueNoToken(incidentId);
    }, 30 * 1000);

    this.onlineHandler = () => {
      this.imageQueueService.processQueueNoToken(incidentId);
    };
    window.addEventListener('online', this.onlineHandler);

    const sub = this.imageQueueService.uploadSuccess$.subscribe(({ localId, document }) => {
      const pending = this.pendingImages.find(img => img.id === localId);
      if (pending) { document.localBase64 = pending.fileBase64; }
      this.incident?.documents.push(document);
      this.pendingImages = this.pendingImages.filter(img => img.id !== localId);
    });

    const errorSub = this.imageQueueService.uploadFailed$.subscribe(({ localId }) => {
      const img = this.pendingImages.find(i => i.id === localId);
      if (img) img.status = 'failed';
    });
    this.queueSubscriptions.push(sub, errorSub);
  }

  private loadIncident(incidentId: number, forceRefresh: boolean = false): void {
    const version = ++this.incidentLoadVersion;
    this.isLoading = true;

    // Create a cache-buster string. If forceRefresh is true, use the current time.
    // Otherwise, use a static string or undefined so normal caching applies.
    const cacheBuster = forceRefresh ? new Date().getTime().toString() : '';

    this.incidentService.getIncidentNoToken(incidentId, 0, cacheBuster).subscribe({
      next: (data) => {
        if (version !== this.incidentLoadVersion || incidentId !== this.currentIncidentId) return;
        this.applyIncident(data);
        this.initImageQueue(data.incidentId);
        this.isLoading = false;
      },
      error: () => {
        if (version !== this.incidentLoadVersion || incidentId !== this.currentIncidentId) return;
        this.isLoading = false;
      }
    });
  }

  private applyIncident(incident: Incident, preserveDraft = false): void {
    const updated = Object.assign(new Incident(), preserveDraft ? this.incident : {}, incident);
    const spLink = updated.incidentClients?.find(l => l.connectionTypeId === 3 && l.isActive);
    const previousSpLink = this.incident?.incidentClients?.find(l => l.connectionTypeId === 3 && l.isActive);
    const previousEstimate = this.incident?.initialTravelTimeMinutes ?? previousSpLink?.incidentTravelTimeMinutes ?? null;
    if (!preserveDraft || this.travelTimeMinutes === previousEstimate) {
      this.travelTimeMinutes = updated.initialTravelTimeMinutes ?? spLink?.incidentTravelTimeMinutes ?? null;
    }
    this.incident = updated;
    this.prepTimeMinutes = spLink?.incidentTravelTimeMinutesAdded ?? 0;
    if (!preserveDraft) this.delayMinutes = 0;
    const location = this.incident.location;
    this.mapUrl = location?.latitude != null && location?.longitude != null
      ? this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`)
      : null;
  }

  public onCommitEta(): void {
    const etaMinutes = this.calculatedEtaMinutes;
    if (!this.incident || etaMinutes == null) return;
    this.isCommittingEta = true;
    const confirm = new EtaConfirm();
    confirm.etaMinutes = etaMinutes;
    confirm.delayMinutes = this.delayMinutes;
    confirm.incidentId = this.incident.incidentId;
    this.incidentService.bdtechConfirmEtaNoToken(confirm).subscribe({
      next: (updated) => {
        this.incident = updated;
        this.isCommittingEta = false;
      },
      error: () => {
        this.isCommittingEta = false;
      }
    });
  }

  public onRefresh(): void {
    this.loadIncident(this.currentIncidentId);
  }

  public onSubscribeToBreakdownUpdates(): void {
    if (!this.incident) return;
    const message = `Subscribe me for updates to breakdown ${this.incident.incidentNumber} at location: ${this.incident.location?.descriptionShort ?? ''}`;
    const whatsappUrl = `https://wa.me/${environment.whatsappSubscriptionNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }

  public onAccept(): void {
    if (!this.incident) return;
    this.isAccepting = true;
    this.incidentService.bdtechAcceptIncidentNoToken(this.incident).subscribe({
      next: (updated) => {
        this.incident = updated;
        this.isAccepting = false;
      },
      error: () => {
        this.isAccepting = false;
      }
    });
  }

  public get incidentUnavailable(): { reason: 'unassigned' | 'closed' } | null {
    if (!this.incident) return null;
    if (this.incident.statusId < 5) return { reason: 'unassigned' };
    if (this.incident.statusId >= 500) return { reason: 'closed' };
    return null;
  }

  public get calculatedEtaMinutes(): number | null {
    if (this.travelTimeMinutes == null || !Number.isFinite(this.travelTimeMinutes) || this.travelTimeMinutes < 0) return null;
    return this.travelTimeMinutes + this.prepTimeMinutes + this.delayMinutes;
  }

  public get calculatedEtaTime(): string {
    const etaMinutes = this.calculatedEtaMinutes;
    if (etaMinutes == null) return '';
    const now = new Date();
    now.setMinutes(now.getMinutes() + etaMinutes);
    return now.toTimeString().substring(0, 5);
  }

  public get etaMinutesRemaining(): number {
    if (!this.incident?.breakdownTechnicianDriverEta) return 0;
    const eta = new Date((this.incident.breakdownTechnicianDriverEta as any) + 'Z');
    const now = new Date();
    return Math.max(0, Math.round((eta.getTime() - now.getTime()) / 60000));
  }

  public getServiceProviderOperator(): IncidentOperator | undefined {
    return this.incident?.incidentOperators?.find(op => op.userType === UserType.callcenterOperator);
  }

  public getTyreLocationString(tyreLocation: number): string {
    switch (tyreLocation) {
      case TyreLocation.steer:   return 'Steer';
      case TyreLocation.drive:   return 'Drive';
      case TyreLocation.trailer: return 'Trailer';
      case TyreLocation.spare:   return 'Spare';
      default:                   return 'Unknown';
    }
  }

  public getTruncatedDescription(): string {
    if (!this.incident?.incidentDescription) return '';
    if (this.incident.incidentDescription.length <= this.DESCRIPTION_MAX_LENGTH) {
      return this.incident.incidentDescription;
    }
    return this.incident.incidentDescription.substring(0, this.DESCRIPTION_MAX_LENGTH) + '...';
  }

  public toggleDescription(): void {
    this.showFullDescription = !this.showFullDescription;
  }

  public toggleDriverDetails(): void {
    this.showDriverDetails = !this.showDriverDetails;
  }

  public toggleOperatorDetails(): void {
    this.showOperatorDetails = !this.showOperatorDetails;
  }

  public getWhatsAppLink(): string {
    if (!this.incident?.driver?.phoneNumber) return '';
    const cleanNumber = this.incident.driver.phoneNumber.replace(/\D/g, '');
    return `https://wa.me/${cleanNumber}`;
  }

  public getOperatorWhatsAppLink(): string {
    const operator = this.getServiceProviderOperator();
    if (!operator?.cellNumber) return '';
    const cleanNumber = operator.cellNumber.replace(/\D/g, '');
    return `https://wa.me/${cleanNumber}`;
  }

  public onClickOpenGoogleMaps(): void {
    if (!this.incident?.location) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${this.incident.location.latitude},${this.incident.location.longitude}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // ===== IMAGES =====

  public hasExistingImages(): boolean {
    const hasUploaded = this.incident?.documents?.some(doc => doc.documentClassificationId === 1) ?? false;
    return hasUploaded || this.pendingImages.length > 0;
  }

  public getImageCount(): number {
    const uploaded = this.incident?.documents?.filter(doc => doc.documentClassificationId === 1).length ?? 0;
    return uploaded + this.pendingImages.length;
  }

  public hasPendingImages(): boolean {
    return this.pendingImages.length > 0;
  }

  public getIncidentImages(): Document[] {
    return this.incident?.documents?.filter(doc => doc.documentClassificationId === 1) ?? [];
  }

  public getCategoryImages(categoryId: number | null): Document[] {
    return this.getIncidentImages().filter(doc => doc.subCategoryId === categoryId);
  }

  public getPendingImagesByCategory(categoryId: number | null): QueuedImage[] {
    return this.pendingImages.filter(img => img.documentClassificationId === 1 && img.subCategoryId === categoryId);
  }

  public onViewImages(): void {
    this.isViewImagesModalVisible = true;
    sessionStorage.setItem(this.MODAL_STATE_KEY, this.currentIncidentId.toString());
  }

  public onRemovePendingImage(localId: number): void {
    this.imageQueueService.removeImage(localId).then(() => {
      this.pendingImages = this.pendingImages.filter(img => img.id !== localId);
    });
  }

  public onRemoveUploadedImage(documentId: number): void {
    if (!this.incident?.incidentId) return;
    this.removingDocumentId = documentId;
    this.incidentService.removeFileFromIncidentNoToken(documentId, this.incident.incidentId).subscribe({
      next: () => {
        this.incident!.documents = this.incident!.documents.filter(doc => doc.documentId !== documentId);
        this.removingDocumentId = null;
        this.notification.success('Success', 'Image removed successfully');
      },
      error: (error) => {
        this.removingDocumentId = null;
        this.notification.error('Error', 'Failed to remove image');
      }
    });
  }

  public closeImagesModal(): void {
    this.isViewImagesModalVisible = false;
    sessionStorage.removeItem(this.MODAL_STATE_KEY);
    sessionStorage.removeItem(this.CAMERA_INTENT_KEY);
  }

  public downloadImage(src: string, filename: string): void {
    if (src.startsWith('data:')) {
      const a = document.createElement('a');
      a.href = src;
      a.download = filename;
      a.click();
      return;
    }
    fetch(src)
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(
        e => {this.notification.error('Download Failed', 'Could not download the image.');
          console.error('Image download error:', e);
        }
      );
  }

  // In your HTML, when they click the button to trigger the camera:
  public triggerCameraClick(cameraInputElement: HTMLInputElement): void {
    sessionStorage.setItem(this.CAMERA_INTENT_KEY, 'true');
    cameraInputElement.click();
  }

  public onCompleteIncident(): void {
    if (this.hasPendingImages()) {
      this.notification.error('Cannot Complete', 'Images are still pending upload. Please wait for uploads to finish.');
      return;
    }
    this.isCompleteIncidentModalVisible = true;
  }

  public onSetArrival(): void {
    if (!this.incident?.incidentId) return;
    this.isSettingArrival = true;
    this.incidentService.breakdownTechnicianArrivedNoToken(this.incident.incidentId).subscribe({
      next: (updated) => { this.incident = updated; this.isSettingArrival = false; },
      error: () => { this.isSettingArrival = false; }
    });
  }

  public onConfirmUpdateArrival(): void {
    if (!this.incident?.incidentId) return;
    this.isSettingArrival = true;
    this.incidentService.breakdownTechnicianArrivedNoToken(this.incident.incidentId).subscribe({
      next: (updated) => {
        this.incident = updated;
        this.isSettingArrival = false;
        this.isUpdateArrivalModalVisible = false;
      },
      error: () => { this.isSettingArrival = false; }
    });
  }

  public onConfirmAddDelay(): void {
    if (!this.incident?.incidentId) return;
    this.isAddingDelay = true;
    this.incidentService.addDelayNoToken(this.incident.incidentId, this.delayModalMinutes, this.delayModalReason).subscribe({
      next: (updated) => {
        this.incident = updated;
        this.isAddingDelay = false;
        this.isAddDelayModalVisible = false;
        this.delayModalMinutes = 15;
        this.delayModalReason = 'Bad Road Conditions';
      },
      error: () => { this.isAddingDelay = false; }
    });
  }

  public onClickProofOfWork(): void {
    if (!this.incident?.incidentId) return;
    this.isLoadingProofOfWork = true;
    this.reportService.generateProofOfWorkReport(this.incident.incidentId).subscribe({
      next: (report: Base64Report) => {
        this.downloadFile(report);
        this.isLoadingProofOfWork = false;
      },
      error: () => {
        this.notification.error('Failed', 'Failed to generate Proof of Work report.');
        this.isLoadingProofOfWork = false;
      }
    });
  }

  private downloadFile(report: Base64Report): void {
    const link = document.createElement('a');
    link.href = 'data:' + report.fileType + ';base64,' + report.base64Data;
    link.download = report.fileName;
    link.click();
  }

  public onConfirmCompleteIncident(): void {
    if (!this.incident?.incidentId) return;
    this.isCompletingIncident = true;
    this.incidentService.breakdownTechnicianCompletedNoToken(this.incident.incidentId).subscribe({
      next: (updated) => {
        this.incident = updated;
        this.isCompletingIncident = false;
        this.isCompleteIncidentModalVisible = false;
      },
      error: () => {
        this.isCompletingIncident = false;
      }
    });
  }

  public async onGallerySelect(event: Event, category?: DocumentCategory | null): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    if (!this.incident?.incidentId) { input.value = ''; return; }
    const files = Array.from(input.files);
    for (const file of files) {
      const compressedBase64 = await this.compressImageHalf(file);
      const localId = await this.imageQueueService.addImage({
        incidentId: this.incident.incidentId,
        fileBase64: compressedBase64,
        name: file.name,
        documentClassificationId: 1,
        createdByUserId: 0,
        dtCreated: new Date(),
        description: category?.categoryDescription || '',
        subCategoryId: category?.categoryId
      });
      this.pendingImages.push({
        id: localId,
        incidentId: this.incident.incidentId,
        fileBase64: compressedBase64,
        name: file.name,
        documentClassificationId: 1,
        createdByUserId: 0,
        dtCreated: new Date(),
        description: category?.categoryDescription || '',
        subCategoryId: category?.categoryId,
        status: 'pending',
        retryCount: 0
      });
    }
    this.imageQueueService.processQueueNoToken(this.incident.incidentId);
    input.value = '';
  }

  public async onCameraCapture(event: Event, category?: DocumentCategory | null): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    if (!this.incident?.incidentId) { input.value = ''; return; }
    const file = input.files[0];
    const compressedBase64 = await this.compressImageHalf(file);
    const localId = await this.imageQueueService.addImage({
      incidentId: this.incident.incidentId,
      fileBase64: compressedBase64,
      name: file.name,
      documentClassificationId: 1,
      createdByUserId: 0,
      dtCreated: new Date(),
      description: category?.categoryDescription || '',
      subCategoryId: category?.categoryId
    });
    this.pendingImages.push({
      id: localId,
      incidentId: this.incident.incidentId,
      fileBase64: compressedBase64,
      name: file.name,
      documentClassificationId: 1,
      createdByUserId: 0,
      dtCreated: new Date(),
      description: category?.categoryDescription || '',
      subCategoryId: category?.categoryId,
      status: 'pending',
      retryCount: 0
    });
    this.imageQueueService.processQueueNoToken(this.incident.incidentId);
    input.value = '';
  }

  private compressImageHalf(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width / 2);
          canvas.height = Math.round(img.height / 2);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
          } else {
            reject(new Error('Failed to get canvas context'));
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  public minuteFormatter = (value: number): string => `${value} min`;
  public minuteParser = (value: string): number => parseInt(value.replace(' min', ''), 10) || 0;
}

